import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { classifyThrownFailure, outcomeFailureProps } from '@buxus/shared'
import { AppBootLoading } from '../components/AppBootLoading'
import { useAbp } from '../platform/abp/react'
import {
  friendlyLoginError,
  LOGIN_ERROR_MODAL_TITLE,
} from '../platform/auth/loginErrorMessage'
import { beginOidcLogin } from '../platform/auth/oidc'
import { EventTypes, trackEvent } from '../platform/analytics/analytics'
import {
  claimLoginStartOidcAttempt,
  clearLoginStartOidcClaim,
  storePendingLoginAnalytics,
} from '../platform/analytics/loginAnalytics'
import { reportReliabilityFailure } from '../platform/errors/webFailureReporting'
import { showAppErrorModal } from '../platform/ui/appErrorModal'
import { LOGIN_PATH, safeLoginReturnTo } from './pathPolicy'

type LoginLocationState = {
  returnTo?: string
}

/** User login entry that skips the local login screen and starts OIDC immediately. */
export function LoginStartRoute() {
  const navigate = useNavigate()
  const location = useLocation()
  const { ready, isAuthenticated } = useAbp()
  const redirectStarted = useRef(false)

  useEffect(() => {
    if (!ready || isAuthenticated) return
    if (redirectStarted.current) return
    // StrictMode remounts reset refs; session claim survives so we do not double-fire analytics/OIDC.
    if (!claimLoginStartOidcAttempt()) return
    redirectStarted.current = true

    void (async () => {
      try {
        trackEvent(
          EventTypes.USER_LOGIN_ATTEMPTED,
          {
            email: '',
            mode: 'user',
            isValid: true,
          },
          { sendImmediately: true },
        )
        storePendingLoginAnalytics({ email: '', mode: 'user' })
        await beginOidcLogin()
      } catch (err: unknown) {
        redirectStarted.current = false
        clearLoginStartOidcClaim()
        const classified = classifyThrownFailure(err)
        reportReliabilityFailure(err, {
          context: 'loginStartOidc',
          failureKind: classified.failureKind,
          analyticsEvent: EventTypes.USER_LOGIN_FLOW_FAILED,
          analyticsProps: {
            email: '',
            mode: 'user',
            ...outcomeFailureProps({
              errorMessage: classified.errorMessage,
              failureKind: classified.failureKind,
            }),
          },
        })
        showAppErrorModal({
          title: LOGIN_ERROR_MODAL_TITLE,
          message: friendlyLoginError(err),
        })
        navigate(LOGIN_PATH, { replace: true, state: location.state })
      }
    })()
  }, [ready, isAuthenticated, navigate, location.state])

  useEffect(() => {
    if (!ready || !isAuthenticated) return
    clearLoginStartOidcClaim()
    const returnTo = safeLoginReturnTo((location.state as LoginLocationState | null)?.returnTo)
    navigate(returnTo, { replace: true })
  }, [ready, isAuthenticated, location.state, navigate])

  const label = !ready ? 'Loading…' : isAuthenticated ? 'Loading…' : 'Redirecting to sign in…'
  return <AppBootLoading label={label} />
}
